import * as React from 'react';

import { Button } from 'components/buttons';

import OverlayHeader from 'components/overlay/OverlayHeader/OverlayHeader';
import OverlayTextWrapper from 'components/overlay/OverlayTextWrapper/OverlayTextWrapper';
import OverlayHeadine from 'components/overlay/OverlayHeadine/OverlayHeadine';
import OverlayParagraph from 'components/overlay/OverlayParagraph/OverlayParagraph';
import { translate } from 'utils/translateUtil';
import rootReducer from 'store/rootReducer';
import { connect } from 'react-redux';
import { Dispatch, bindActionCreators } from 'redux';

import { modalStyles as styles, modalActions } from '.';

export enum ModalStyle {
	success = 'success',
	error = 'error',
	pending = 'pending'
}

export enum ButtonStyle {
	retry = 'retry',
	confirm = 'confirm',
	choose = 'choose',
	pending = 'pending'
}

export const ModalTemplates = {
	no2fa: {
		modalStyle: 'error',
		textHeadline: translate('errors.modal.no2FA.textHeadline'),
		textDescription: translate('errors.modal.no2FA.textParagraph')
	},
	badConnection: {
		modalStyle: 'error'
	},
	ibanBlacklisted: {
		modalStyle: 'error',
		textHeadline: translate('errors.modal.ibanBlacklisted.textHeadline'),
		textDescription: translate('errors.modal.ibanBlacklisted.textParagraph')
	}
};

export interface ModalOptions {
	textConfirm?: string;
	textCancel?: string;
	textAction?: string;

	textHeadline?: string;
	textDescription?: string;

	visible?: boolean;
	template?: 'no2fa' | 'badConnection' | 'ibanBlacklisted';

	modalStyle?: ModalStyle; // undefined renders custom header
	buttonStyle?: ButtonStyle; // undefined renders custom footer

	onConfirm?: () => void;
	onCancel?: () => void;
	onAction?: () => void;

	onClose?: () => void;
}

interface ModalComposition {
	renderHeader?: React.ReactNode;
	renderContent?: React.ReactNode;
	renderFooter?: React.ReactNode;
}

type ModalProps = ModalComposition & ModalOptions & { actions?: any };

class ModalContainer extends React.Component<ModalProps> {
	public getCTA() {
		const {
			actions,
			buttonStyle = ButtonStyle.confirm,
			textAction = translate('default.button.textAction'),
			textCancel = translate('default.button.textCancel'),
			textConfirm = translate('default.button.textConfirm'),
			onAction = () => actions.hideModal(),
			onConfirm = () => actions.hideModal(),
			onCancel = () => actions.hideModal()
		} = this.props;

		switch (buttonStyle) {
			case ButtonStyle.confirm:
				return (
					<Button name={'confirm'} clickHandler={onConfirm} className={styles.button}>
						{textConfirm}
					</Button>
				);
			case ButtonStyle.choose:
				return (
					<>
						<Button
							name={'cancel'}
							theme={'BTN_TERTIARY'}
							clickHandler={onCancel}
							className={styles.button}
						>
							{textCancel}
						</Button>
						<Button name={'ok'} clickHandler={onConfirm} className={styles.button}>
							{textConfirm}
						</Button>
					</>
				);
			case ButtonStyle.retry:
				return (
					<Button name={'retry'} clickHandler={onAction} className={styles.button}>
						{textAction}
					</Button>
				);
			default:
				return this.props.renderFooter;
		}
	}

	public render() {
		const { template, modalStyle = 'success', ...props } = this.props;

		// console.log('Changed', template);

		const defaults = {
			...props,
			...(template && ModalTemplates[template])
		};

		const {
			renderHeader,
			renderContent,
			renderFooter,
			visible = false,
			buttonStyle = ButtonStyle.confirm,
			textHeadline = translate(`default.modal.${ModalStyle[modalStyle]}.textHeadline`),
			textDescription = translate(`default.modal.${ModalStyle[modalStyle]}.textParagraph`)
		} = defaults;

		return (
			visible && (
				<div className={`${styles.wrapper}`}>
					<div className={`${styles.elem} ${styles[buttonStyle]}`}>
						{renderHeader || <OverlayHeader overlayState={modalStyle} />}

						<OverlayTextWrapper className={styles.textWrapper}>
							<OverlayHeadine noPaddingBottom={false}>{textHeadline}</OverlayHeadine>
							{renderContent || <OverlayParagraph>{textDescription}</OverlayParagraph>}
							{renderFooter || <div className={`${styles.btnWrapper}`}>{this.getCTA()}</div>}
						</OverlayTextWrapper>
					</div>
				</div>
			)
		);
	}
}

// Container is connected with the store. Thus the actual working Modal is
// exported here, but the container (without store) is tested.
const mapStateToProps = ({ modal }: ReturnType<typeof rootReducer>) => modal;

const mapDispatchToProps = (dispatch: Dispatch) => ({
	actions: bindActionCreators(modalActions, dispatch)
});

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(ModalContainer);
